import type { BrandSpecial } from '@iheartjane/data-access';
import { useContextEnsuringProvider } from '@iheartjane/utils';
import difference from 'lodash-es/difference';
import isEqual from 'lodash-es/isEqual';
import uniq from 'lodash-es/uniq';
import { createContext, useEffect, useMemo, useState } from 'react';
import type { PropsWithChildren } from 'react';

import type { RootLoaderType } from '@jane/kiosk/data-access';
import {
  useKioskUser,
  useTargetedBrandSpecialsForStore,
} from '@jane/kiosk/data-access';
import {
  useKioskCart,
  useShouldKioskShowGold,
  useTypedRouteLoaderData,
} from '@jane/kiosk/hooks';
import { buildBrandSpecialFilter } from '@jane/search/util';

interface BrandSpecialsContextCart {
  brand_discounts_total?: number | null;
  brand_special_has_changed?: boolean;
  products?: { brand_special_id?: number | null }[];
}

export interface BrandSpecialsContext {
  addedBrandSpecialIds: number[];
  brandSpecialFilter: string;
  brandSpecials: BrandSpecial[];
  cart: BrandSpecialsContextCart | null;
  loadingBrandSpecials: boolean;
  removedBrandSpecialIds: number[];
  shouldShowAlert: boolean;
}

export const BrandSpecialsContext = createContext<BrandSpecialsContext | null>(
  null
);
BrandSpecialsContext.displayName = 'BrandSpecials';

export const BrandSpecialsProvider = ({ children }: PropsWithChildren) => {
  const [cartBrandSpecialIds, setCartBrandSpecialIds] = useState<number[]>([]);
  const [addedBrandSpecialIds, setAddedBrandSpecialIds] = useState<number[]>(
    []
  );
  const [removedBrandSpecialIds, setRemovedBrandSpecialIds] = useState<
    number[]
  >([]);

  const { storeId } = useTypedRouteLoaderData<RootLoaderType>('root');

  const shouldShowGold = useShouldKioskShowGold();

  const { data: kioskUser } = useKioskUser();
  const { data: cart } = useKioskCart();
  const {
    data,
    isFetching: isFetchingBrandSpecials,
    refetch: refetchBrandSpecials,
  } = useTargetedBrandSpecialsForStore(storeId, {
    enabled: shouldShowGold,
  });

  const brandSpecials = useMemo(() => data?.brand_specials ?? [], [data]);

  const brandSpecialIds = useMemo(
    () => brandSpecials?.map(({ id }) => id),
    [brandSpecials]
  );

  const brandSpecialFilter = useMemo(
    () => buildBrandSpecialFilter(brandSpecialIds),
    [brandSpecialIds]
  );

  useEffect(() => {
    const filteredIds =
      cart?.products && cart.products.length > 0
        ? cart.products
            .map(({ brand_special_id }) => brand_special_id)
            .filter(
              (id: number | undefined | null) => id !== null && id !== undefined
            )
            .sort((a, b) => a - b)
        : [];
    const newBrandSpecialIds = uniq<number>(filteredIds);

    if (!isEqual(cartBrandSpecialIds, newBrandSpecialIds)) {
      const addedIds = difference<number>(
        newBrandSpecialIds,
        cartBrandSpecialIds
      );
      setAddedBrandSpecialIds(addedIds);

      const removedIds = difference<number>(
        cartBrandSpecialIds,
        newBrandSpecialIds
      );
      setRemovedBrandSpecialIds(removedIds);

      setCartBrandSpecialIds(newBrandSpecialIds);
    } else if (
      addedBrandSpecialIds?.length > 0 ||
      removedBrandSpecialIds?.length > 0
    ) {
      setAddedBrandSpecialIds([]);
      setRemovedBrandSpecialIds([]);
    }
  }, [cart, cartBrandSpecialIds, addedBrandSpecialIds, removedBrandSpecialIds]);

  useEffect(() => {
    if (shouldShowGold) {
      refetchBrandSpecials();
    }
  }, [kioskUser]);

  const context = useMemo(
    () => ({
      addedBrandSpecialIds,
      brandSpecialFilter,
      brandSpecials,
      cart: cart ?? null,
      loadingBrandSpecials: isFetchingBrandSpecials,
      removedBrandSpecialIds,
      shouldShowAlert: Boolean(
        cart?.brand_special_has_changed &&
        (addedBrandSpecialIds?.length > 0 || removedBrandSpecialIds?.length > 0)
      ),
    }),
    [
      addedBrandSpecialIds,
      brandSpecialFilter,
      brandSpecials,
      cart,
      isFetchingBrandSpecials,
      removedBrandSpecialIds,
    ]
  );

  return (
    <BrandSpecialsContext.Provider value={context}>
      {children}
    </BrandSpecialsContext.Provider>
  );
};

export const useBrandSpecials = () =>
  useContextEnsuringProvider(BrandSpecialsContext);
